// Right-column timeline pane for a track: bar grid, sub-beat grid, waveform
// peaks (clipped to the loop window), onset markers or drag-editable "note blocks",
// live playhead, drag snap-line preview and the right-click "Duplicate to bar N"
// context menu. Split out of TrackRow.jsx.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Copy } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { kindOf } from "./constants";
import { classifierSuggestions, previewNoteSegments } from "@/lib/audio";

const SIXTEENTH_PER_BAR = 16;

export default function TrackTimeline({
  track, loopBars, loopDuration, playing, playPos, bpm,
  nudgeMode,
  onNudgeOnset, onDeleteOnset, onDuplicateOnset, onDuplicateToBars,
  onDeleteMany, onDuplicateMany, onClassifyOnset, onNudgeMany, onResizeOnset,
  onPitchShiftOnset, onPitchShiftMany, onAutoTune, onPasteAtTime,
  keyRoot, keyMode, onChangeKey,
}) {
  const k = kindOf(track.kind);
  const peaks = track.peaks || [];
  // Memoized so hooks depending on `onsets` don't re-fire on unrelated renders
  // (the `|| []` fallback would otherwise mint a fresh array identity each time).
  const onsets = useMemo(() => track.onsets || [], [track.onsets]);
  const hitOverrides = track.hit_overrides || {};
  const noteLengths = track.note_lengths || {};
  const trackDur = track.duration || 0;
  // Auto-tune key is the project-level key (persisted via `onChangeKey`) so
  // Chordify and Auto-tune always stay diatonic to the same scale. Defaults
  // to C major when the project hasn't set a key yet.
  const activeKeyRoot = typeof keyRoot === "number" ? keyRoot : 0;
  const activeKeyMode = typeof keyMode === "string" ? keyMode : "major";

  // ---- Classifier suggestions (dim K/S/H over each un-overridden note) ----
  // Only computed while edit-mode is on — running detectOnsets with centroid
  // data has real cost and we don't want to pay it for every track on page load.
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    if (!nudgeMode || !track.buffer) {
      setSuggestions([]);
      return;
    }
    // Kick to a microtask so the "Edit notes" toggle animates instantly.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setSuggestions(classifierSuggestions(track.buffer));
    });
    return () => { cancelled = true; };
  }, [nudgeMode, track.buffer]);
  const pct = loopDuration > 0 ? Math.min(1, playPos / loopDuration) : 0;
  const fillPct = loopDuration > 0 ? Math.min(1, trackDur / loopDuration) : 0;

  const visiblePeakCount = trackDur > loopDuration && loopDuration > 0
    ? Math.max(1, Math.floor((peaks.length * loopDuration) / trackDur))
    : peaks.length;
  const visiblePeaks = peaks.slice(0, visiblePeakCount);
  const visibleTrackDur = Math.min(trackDur, loopDuration);
  const peakFillPct = loopDuration > 0 ? visibleTrackDur / loopDuration : 0;

  const bars = Math.max(1, loopBars || 4);
  const barLines = Array.from({ length: bars + 1 }, (_, i) => (i / bars) * 100);
  const beatLines = Array.from({ length: bars * 4 + 1 }, (_, i) => (i / (bars * 4)) * 100);

  const timelineRef = useRef(null);
  const snapStep = loopDuration > 0 && bars > 0 ? loopDuration / (bars * SIXTEENTH_PER_BAR) : 0;

  // ---- Drag-to-quantize state ----
  // Also carries group-drag data when the dragged note is part of a 2+ selection
  // (drag.groupIndices + drag.groupOriginals). All notes in the group move by
  // the same delta and the group commit fires onNudgeMany on mouseup.
  const [drag, setDrag] = useState(null);
  // Set when a drag actually moved a note — the click that follows mouseup must
  // not trigger the note audition.
  const suppressAuditionRef = useRef(false);
  // Guards arrow-key nudges: one in flight at a time (cleared when onsets update).
  const keyNudgeBusyRef = useRef(false);
  useEffect(() => {
    keyNudgeBusyRef.current = false;
  }, [onsets]);

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      const rect = timelineRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || loopDuration <= 0) return;
      const deltaSec = ((e.clientX - drag.startX) / rect.width) * loopDuration;
      let target = drag.originalTime + deltaSec;
      target = Math.max(0, Math.min(loopDuration, target));
      if (snapStep > 0) target = Math.round(target / snapStep) * snapStep;
      setDrag((d) => (d ? { ...d, currentTime: target } : d));
    };
    const onUp = () => {
      const d = drag;
      setDrag(null);
      if (!d) return;
      const delta = (d.currentTime ?? d.originalTime) - d.originalTime;
      if (Math.abs(delta) < 0.005) return;
      // Group-drag: shift all selected notes by the same delta. Otherwise nudge
      // just the single dragged note to its new absolute time.
      if (d.groupIndices && d.groupIndices.length >= 2 && onNudgeMany) {
        onNudgeMany(d.groupIndices, delta);
      } else if (onNudgeOnset) {
        onNudgeOnset(d.onsetIndex, d.currentTime);
      }
      // A real move committed — the trailing click event shouldn't audition.
      suppressAuditionRef.current = true;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [drag, loopDuration, snapStep, onNudgeOnset, onNudgeMany]);

  function beginDrag(e, onsetIndex) {
    if (!nudgeMode) return;
    // If a selection modifier is held, treat this as a click for multi-select
    // (see `handleNoteClick`) — don't start a drag.
    if (e.shiftKey || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    e.stopPropagation();
    // Group-drag: if the dragged note is part of a 2+ selection, capture the
    // whole set and each original onset time so we can preview all shifted
    // together while dragging.
    const isGroup = selectedIndices.size >= 2 && selectedIndices.has(onsetIndex);
    const groupIndices = isGroup ? [...selectedIndices] : null;
    const groupOriginals = groupIndices
      ? Object.fromEntries(groupIndices.map((i) => [i, onsets[i]]))
      : null;
    setDrag({
      onsetIndex,
      startX: e.clientX,
      originalTime: onsets[onsetIndex],
      currentTime: onsets[onsetIndex],
      groupIndices,
      groupOriginals,
    });
  }

  // ---- Right-edge resize (drag the block's right edge to change note length) ----
  const [resize, setResize] = useState(null); // { onsetIndex, startX, originalLen, currentLen }

  useEffect(() => {
    if (!resize) return undefined;
    const onMove = (e) => {
      const rect = timelineRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || loopDuration <= 0) return;
      const deltaSec = ((e.clientX - resize.startX) / rect.width) * loopDuration;
      const orig = onsets[resize.onsetIndex];
      const nextOn = resize.onsetIndex + 1 < onsets.length ? onsets[resize.onsetIndex + 1] : loopDuration;
      // Don't allow overlapping the next note — leave at least 20 ms breathing room.
      const maxLen = Math.max(0.02, nextOn - orig - 0.02);
      let newLen = resize.originalLen + deltaSec;
      newLen = Math.max(0.02, Math.min(newLen, maxLen, 4.0));
      setResize((r) => (r ? { ...r, currentLen: newLen } : r));
    };
    const onUp = () => {
      const r = resize;
      setResize(null);
      if (!r) return;
      if (Math.abs(r.currentLen - r.originalLen) < 0.005) return;
      if (onResizeOnset) onResizeOnset(r.onsetIndex, r.currentLen);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [resize, loopDuration, onsets, onResizeOnset]);

  function beginResize(e, onsetIndex) {
    if (!nudgeMode) return;
    e.preventDefault();
    e.stopPropagation();
    const orig = onsets[onsetIndex];
    if (typeof orig !== "number") return;
    const nextOn = onsetIndex + 1 < onsets.length ? onsets[onsetIndex + 1] : loopDuration;
    const defaultLen = Math.min(nextOn - orig, 0.5, loopDuration - orig);
    const explicit = noteLengths[onsetIndex] ?? noteLengths[String(onsetIndex)];
    const originalLen = typeof explicit === "number" ? explicit : defaultLen;
    setResize({ onsetIndex, startX: e.clientX, originalLen, currentLen: originalLen });
  }

  // ---- Right-click "Duplicate to bar N" menu ----
  const [noteCtx, setNoteCtx] = useState(null); // { onsetIndex, x, y }
  useEffect(() => {
    if (!noteCtx) return undefined;
    const onAway = () => setNoteCtx(null);
    const onKey = (e) => { if (e.key === "Escape") setNoteCtx(null); };
    document.addEventListener("mousedown", onAway);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [noteCtx]);

  function openNoteCtx(e, onsetIndex) {
    if (!nudgeMode) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;
    setNoteCtx({
      onsetIndex,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }

  // ---- Multi-select (Shift-click / Cmd-click / Ctrl-click) ----
  const [selectedIndices, setSelectedIndices] = useState(() => new Set());

  // Turning Edit-mode OFF should clear any leftover selection so the highlight
  // doesn't survive into read-only view.
  useEffect(() => {
    if (!nudgeMode) setSelectedIndices(new Set());
  }, [nudgeMode]);

  const batchDeleteSelected = useCallback(async () => {
    const arr = [...selectedIndices];
    if (!arr.length) return;
    if (onDeleteMany) await onDeleteMany(arr);
    setSelectedIndices(new Set());
  }, [selectedIndices, onDeleteMany]);

  const batchDuplicateSelected = useCallback(async () => {
    const arr = [...selectedIndices];
    if (!arr.length) return;
    if (onDuplicateMany) await onDuplicateMany(arr);
    setSelectedIndices(new Set());
  }, [selectedIndices, onDuplicateMany]);

  // Select every onset in this track — respects the visible loop window so we
  // don't quietly select silent notes past the 4-bar mark (they wouldn't play
  // in loop mode anyway and users can't see them).
  const selectAllOnsets = useCallback(() => {
    if (!nudgeMode) return;
    const all = new Set();
    for (let i = 0; i < onsets.length; i++) {
      if (onsets[i] <= loopDuration) all.add(i);
    }
    setSelectedIndices(all);
  }, [nudgeMode, onsets, loopDuration]);

  // ---- Copy / paste (selection → another bar) ----
  // Clipboard stores the copied notes' ABSOLUTE times (not indices) — indices
  // shift whenever the buffer is re-analyzed after an edit, so each paste
  // re-maps times back to current onset indices with a 15 ms tolerance. That
  // keeps the clipboard valid across multiple pastes into different bars.
  const [clipboard, setClipboard] = useState(null); // { times: number[] }
  // When armed, the next click on the timeline pastes the clipboard there.
  const [pasteArmed, setPasteArmed] = useState(false);
  useEffect(() => {
    if (!nudgeMode) {
      setClipboard(null);
      setPasteArmed(false);
    }
  }, [nudgeMode]);

  const copySelected = useCallback(() => {
    const times = [...selectedIndices]
      .map((i) => onsets[i])
      .filter((t) => typeof t === "number")
      .sort((a, b) => a - b);
    if (!times.length) return;
    setClipboard({ times });
  }, [selectedIndices, onsets]);

  const pasteAtTime = useCallback(async (anchorTime) => {
    if (!clipboard || !onPasteAtTime) return;
    const indices = [];
    for (const t of clipboard.times) {
      let best = -1;
      let bestD = 0.015;
      for (let i = 0; i < onsets.length; i++) {
        const d = Math.abs(onsets[i] - t);
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best >= 0 && !indices.includes(best)) indices.push(best);
    }
    setPasteArmed(false);
    if (!indices.length) { setClipboard(null); return; }
    await onPasteAtTime(indices, anchorTime);
  }, [clipboard, onsets, onPasteAtTime]);

  // Keyboard shortcuts while a selection exists: Esc clears, Delete/Backspace
  // triggers the batch-delete flow, Cmd/Ctrl+C copies the selection to the
  // paste clipboard. Ignored when the user is typing in an input.
  useEffect(() => {
    if (selectedIndices.size === 0) return undefined;
    function onKey(e) {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if (e.key === "Escape") {
        setSelectedIndices(new Set());
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        batchDeleteSelected();
      } else if ((e.metaKey || e.ctrlKey) && (e.key === "c" || e.key === "C")) {
        e.preventDefault();
        copySelected();
      } else if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && snapStep > 0) {
        // Fine timing: nudge the selection by one grid step per press.
        e.preventDefault();
        if (keyNudgeBusyRef.current) return;
        const delta = e.key === "ArrowLeft" ? -snapStep : snapStep;
        const sel = [...selectedIndices];
        if (sel.length >= 2 && onNudgeMany) {
          keyNudgeBusyRef.current = true;
          onNudgeMany(sel, delta);
        } else if (onNudgeOnset) {
          const i = sel[0];
          const target = Math.max(0, Math.min(loopDuration, (onsets[i] ?? 0) + delta));
          if (Math.abs(target - (onsets[i] ?? 0)) < 0.001) return;
          keyNudgeBusyRef.current = true;
          onNudgeOnset(i, target);
        }
        // Safety: never leave the guard stuck if the nudge no-ops upstream.
        setTimeout(() => { keyNudgeBusyRef.current = false; }, 3000);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedIndices, batchDeleteSelected, copySelected, snapStep, loopDuration, onsets, onNudgeMany, onNudgeOnset]);

  // Cmd/Ctrl + A while in edit mode selects every note on this track.
  useEffect(() => {
    if (!nudgeMode) return undefined;
    function onKey(e) {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if ((e.metaKey || e.ctrlKey) && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        selectAllOnsets();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [nudgeMode, selectAllOnsets]);

  function handleNoteClick(e, onsetIndex) {
    if (!nudgeMode) return;
    e.stopPropagation();
    // Note audition: a plain tap plays just this note so you can hear it before
    // editing — skipped when the click is the tail end of a drag-move.
    if (suppressAuditionRef.current) {
      suppressAuditionRef.current = false;
    } else if (track?.buffer) {
      previewNoteSegments(track.buffer, onsets, [onsetIndex], noteLengths, track.volume ?? 1);
    }
    const modifier = e.shiftKey || e.metaKey || e.ctrlKey;
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (modifier) {
        // Shift / Cmd / Ctrl → toggle this index in/out of the current selection.
        if (next.has(onsetIndex)) next.delete(onsetIndex);
        else next.add(onsetIndex);
      } else {
        // Plain click → replace selection with just this note (or clear if the
        // user re-clicked the sole selected note — small ergonomic touch).
        if (next.size === 1 && next.has(onsetIndex)) return new Set();
        return new Set([onsetIndex]);
      }
      return next;
    });
  }

  // Clicking the timeline: in armed-paste mode, paste at the clicked position
  // (snapped to the grid); otherwise a blank click clears the selection. Note
  // blocks stopPropagation, so clicks landing here never come from a note.
  function handleTimelineBlankClick(e) {
    if (!nudgeMode) return;
    if (pasteArmed && clipboard) {
      const rect = timelineRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || loopDuration <= 0) return;
      let t = ((e.clientX - rect.left) / rect.width) * loopDuration;
      if (snapStep > 0) t = Math.round(t / snapStep) * snapStep;
      t = Math.max(0, Math.min(loopDuration, t));
      pasteAtTime(t);
      return;
    }
    if (e.target !== timelineRef.current) return;
    setSelectedIndices(new Set());
  }

  const ctxSourceBar = noteCtx && bpm > 0
    ? Math.floor((onsets[noteCtx.onsetIndex] ?? 0) / (60 / bpm * 4))
    : -1;

  // Cursor state: extracted from nested ternary for readability.
  let cursor = "default";
  if (nudgeMode) cursor = drag ? "grabbing" : "crosshair";

  return (
    <div
      ref={timelineRef}
      className="col-span-12 md:col-span-9 relative bg-[#141517] overflow-hidden min-h-[260px] md:min-h-0"
      data-testid={`track-timeline-${track.id}`}
      style={{ cursor }}
      onClick={handleTimelineBlankClick}
    >
      {/* Multi-select toolbar — always visible in edit mode so users have a
          one-tap "Select all" affordance (no need to shift-click every note),
          then extra Duplicate / Delete / Clear appear once notes are selected. */}
      {nudgeMode && onsets.length > 0 && (
        <div
          className="absolute top-1 right-2 z-30 flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#1A1B1D]/95 border border-[#60A5FA]/60 backdrop-blur-sm"
          data-testid={`track-${track.id}-multiselect-bar`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#60A5FA]">
            {selectedIndices.size > 0 ? `${selectedIndices.size} selected` : `${onsets.length} notes`}
          </span>
          <button
            type="button"
            onClick={selectAllOnsets}
            data-testid={`track-${track.id}-multiselect-all`}
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#60A5FA]/20 text-[#60A5FA] border border-[#60A5FA]/60 hover:bg-[#60A5FA]/30 transition-colors"
            title="Select all notes (Cmd/Ctrl + A)"
          >
            Select all
          </button>
          {selectedIndices.size > 0 && (
            <>
              <button
                type="button"
                onClick={batchDuplicateSelected}
                data-testid={`track-${track.id}-multiselect-duplicate`}
                className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#60A5FA] text-[#0A0A0A] hover:bg-[#3B82F6] transition-colors"
                title="Duplicate all selected notes one grid step to the right"
              >
                Duplicate
              </button>
              {onPitchShiftMany && (
                <>
                  <button
                    type="button"
                    onClick={async () => {
                      const arr = [...selectedIndices];
                      if (arr.length) await onPitchShiftMany(arr, -1);
                    }}
                    data-testid={`track-${track.id}-multiselect-pitch-down`}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#22C55E]/20 text-[#4ADE80] border border-[#4ADE80]/60 hover:bg-[#22C55E]/30 transition-colors"
                    title="Pitch selected notes down 1 semitone"
                  >
                    ♭ Pitch
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const arr = [...selectedIndices];
                      if (arr.length) await onPitchShiftMany(arr, +1);
                    }}
                    data-testid={`track-${track.id}-multiselect-pitch-up`}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#22C55E]/20 text-[#4ADE80] border border-[#4ADE80]/60 hover:bg-[#22C55E]/30 transition-colors"
                    title="Pitch selected notes up 1 semitone"
                  >
                    ♯ Pitch
                  </button>
                </>
              )}
              {onPasteAtTime && (
                <button
                  type="button"
                  onClick={copySelected}
                  data-testid={`track-${track.id}-multiselect-copy`}
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FBBF24]/20 text-[#FBBF24] border border-[#FBBF24]/60 hover:bg-[#FBBF24]/30 transition-colors"
                  title="Copy selected notes (Cmd/Ctrl + C) — then hit Paste and click where they should land"
                >
                  Copy
                </button>
              )}
              <button
                type="button"
                onClick={batchDeleteSelected}
                data-testid={`track-${track.id}-multiselect-delete`}
                className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E11D48] text-white hover:bg-[#BE123C] transition-colors"
                title="Delete all selected notes"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setSelectedIndices(new Set())}
                data-testid={`track-${track.id}-multiselect-clear`}
                className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#34373B] text-[#9CA0A6] hover:text-white hover:border-white transition-colors"
                title="Clear selection (Esc)"
              >
                Clear
              </button>
              {onAutoTune && (
                <span className="flex items-center gap-1 pl-1 border-l border-[#34373B]">
                  <select
                    value={activeKeyRoot}
                    onChange={(e) => onChangeKey && onChangeKey({ key_root: parseInt(e.target.value, 10) })}
                    data-testid={`track-${track.id}-multiselect-autotune-root`}
                    className="text-[10px] font-mono uppercase tracking-widest bg-transparent text-[#4ADE80] border border-[#4ADE80]/60 rounded-full px-1.5 py-0.5 hover:bg-[#22C55E]/20 transition-colors focus:outline-none"
                    title="Project key root (used by Auto-tune + Chordify)"
                  >
                    {["C","C♯","D","D♯","E","F","F♯","G","G♯","A","A♯","B"].map((name, i) => (
                      <option key={name} value={i}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={activeKeyMode}
                    onChange={(e) => onChangeKey && onChangeKey({ key_mode: e.target.value })}
                    data-testid={`track-${track.id}-multiselect-autotune-mode`}
                    className="text-[10px] font-mono uppercase tracking-widest bg-transparent text-[#4ADE80] border border-[#4ADE80]/60 rounded-full px-1.5 py-0.5 hover:bg-[#22C55E]/20 transition-colors focus:outline-none"
                    title="Scale — Chordify + Auto-tune stay diatonic to this"
                  >
                    <option value="major">maj</option>
                    <option value="minor">min</option>
                    <option value="dorian">dor</option>
                    <option value="mixolydian">mix</option>
                    <option value="pentatonic">pent</option>
                  </select>
                  <button
                    type="button"
                    onClick={async () => {
                      const arr = [...selectedIndices];
                      if (arr.length) await onAutoTune(arr, activeKeyRoot, activeKeyMode);
                    }}
                    data-testid={`track-${track.id}-multiselect-autotune`}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#4ADE80] text-[#0A0A0A] hover:bg-[#22C55E] font-semibold transition-colors"
                    title="Auto-tune selected notes to the chosen key"
                  >
                    Auto-tune
                  </button>
                </span>
              )}
            </>
          )}
          {clipboard && onPasteAtTime && (
            <span
              className="flex items-center gap-1.5 pl-1 border-l border-[#34373B]"
              data-testid={`track-${track.id}-paste-group`}
            >
              <button
                type="button"
                onClick={() => setPasteArmed((a) => !a)}
                data-testid={`track-${track.id}-paste-btn`}
                className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full font-semibold transition-colors ${
                  pasteArmed
                    ? "bg-[#FBBF24] text-[#0A0A0A] hover:bg-[#F59E0B]"
                    : "bg-[#FBBF24]/20 text-[#FBBF24] border border-[#FBBF24]/60 hover:bg-[#FBBF24]/30"
                }`}
                title="Paste the copied notes — click this, then click the spot on the track where they should land (snapped to the grid)"
              >
                Paste {clipboard.times.length}
              </button>
              {pasteArmed && (
                <span
                  className="text-[10px] font-mono uppercase tracking-widest text-[#FBBF24] animate-pulse"
                  data-testid={`track-${track.id}-paste-hint`}
                >
                  Click a spot on the track
                </span>
              )}
              <button
                type="button"
                onClick={() => { setClipboard(null); setPasteArmed(false); }}
                data-testid={`track-${track.id}-paste-cancel`}
                className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-[#34373B] text-[#9CA0A6] hover:text-white hover:border-white transition-colors"
                title="Discard copied notes"
              >
                ✕
              </button>
            </span>
          )}
        </div>
      )}
      {/* Bar number labels */}
      <div className="absolute top-1 left-0 right-0 h-4 flex items-start pointer-events-none">
        {Array.from({ length: bars }, (_, i) => (
          <div
            key={`bar-label-${i}`}
            className="flex-1 text-[10px] font-mono uppercase tracking-widest text-[#5A5E63] pl-1.5"
            data-testid={`track-${track.id}-bar-label-${i + 1}`}
          >
            bar {i + 1}
          </div>
        ))}
      </div>
      {/* Sub-beat grid */}
      <div className="absolute inset-0 pointer-events-none">
        {beatLines.map((p, i) => (
          <div
            key={`beat-${i}`}
            className="absolute top-0 bottom-0 w-px"
            style={{
              left: `${p}%`,
              background: i % 4 === 0 ? "transparent" : "rgba(255,255,255,0.04)",
            }}
          />
        ))}
      </div>
      {/* Bar lines */}
      <div className="absolute inset-0 pointer-events-none">
        {barLines.map((p, i) => (
          <div
            key={`bar-${i}`}
            className="absolute top-0 bottom-0 w-px"
            style={{
              left: `${p}%`,
              background: i === 0 || i === barLines.length - 1
                ? "rgba(255,255,255,0.10)"
                : "rgba(168,255,31,0.18)",
            }}
          />
        ))}
      </div>
      {/* Recording-extent shading */}
      {trackDur > 0 && (
        <div
          className="absolute top-5 bottom-1 rounded-sm pointer-events-none"
          style={{
            left: 0,
            width: `${fillPct * 100}%`,
            background: `${k.c}0F`,
          }}
        />
      )}
      {/* Waveform peaks */}
      {visiblePeaks.length > 0 && peakFillPct > 0 && (
        <div
          className="absolute top-5 bottom-1 overflow-hidden"
          style={{ left: 0, width: `${peakFillPct * 100}%` }}
        >
          {visiblePeaks.map((p, i) => {
            const h = Math.max(2, p * 60);
            const leftPct = (i / visiblePeaks.length) * 100;
            const peakTime = (i / visiblePeaks.length) * visibleTrackDur;
            const active = playing && peakTime < playPos;
            return (
              <div
                key={`peak-${track.id}-${i}`}
                className="absolute rounded-sm"
                style={{
                  left: `${leftPct}%`,
                  width: `calc(${100 / visiblePeaks.length}% - 1px)`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  height: `${h}px`,
                  background: active ? k.c : `${k.c}66`,
                  opacity: track.muted ? 0.25 : 1,
                }}
              />
            );
          })}
        </div>
      )}
      {trackDur > 0 && peaks.length === 0 && (
        <div className="absolute top-5 left-2 text-[#5A5E63] font-mono text-xs uppercase tracking-widest">
          no audio data
        </div>
      )}
      {/* Onset markers (ticks in read mode, note-blocks in nudge mode) */}
      <div
        className={`absolute top-5 left-0 right-0 ${nudgeMode ? "bottom-1" : "h-2"}`}
        data-testid={`track-${track.id}-onsets`}
        style={{ pointerEvents: nudgeMode ? "none" : "none" }}
      >
        {onsets.map((time, i) => {
          if (time > loopDuration) return null;
          const isDragging = drag && drag.onsetIndex === i;
          // Group-drag: any OTHER selected note in the same drag session should
          // preview at its original time shifted by the same delta.
          const isGroupMember = drag && drag.groupIndices && drag.groupIndices.includes(i) && drag.onsetIndex !== i;
          let displayTime = time;
          if (isDragging) {
            displayTime = drag.currentTime;
          } else if (isGroupMember) {
            const delta = drag.currentTime - drag.originalTime;
            displayTime = Math.max(0, Math.min(loopDuration, (drag.groupOriginals?.[i] ?? time) + delta));
          }
          const leftPct = (displayTime / loopDuration) * 100;
          if (nudgeMode) {
            // Length precedence: live-resize preview → persisted note_lengths →
            // legacy default (next-onset or 0.5 s cap). Same fallback used by
            // _segmentMeta in audio.js so visual and audio stay in sync.
            const nextTime = i + 1 < onsets.length ? onsets[i + 1] : loopDuration;
            const explicit = noteLengths[i] ?? noteLengths[String(i)];
            const defaultLen = Math.min(nextTime - time, 0.5, loopDuration - time);
            const isResizing = resize && resize.onsetIndex === i;
            let rawLen = defaultLen;
            if (isResizing) rawLen = resize.currentLen;
            else if (typeof explicit === "number") rawLen = explicit;
            const rawWidthPct = Math.max(0.5, (rawLen / loopDuration) * 100);
            const isSelected = selectedIndices.has(i);
            const hasCustomLength = typeof explicit === "number";
            return (
              <div
                key={`onset-${track.id}-${i}`}
                className="absolute pointer-events-auto group"
                style={{
                  left: `${leftPct}%`,
                  width: `${rawWidthPct}%`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  height: "44px",
                }}
              >
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); if (onDuplicateOnset) onDuplicateOnset(i); }}
                    data-testid={`track-${track.id}-note-duplicate-${i}`}
                    className="w-5 h-5 rounded-full bg-[#60A5FA] text-white inline-flex items-center justify-center"
                    aria-label="Duplicate note"
                    title="Copy this note one 1/16 step to the right"
                  >
                    <Copy size={10} weight="bold" />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); if (onDeleteOnset) onDeleteOnset(i); }}
                    data-testid={`track-${track.id}-note-delete-${i}`}
                    className="w-5 h-5 rounded-full bg-[#E11D48] text-white inline-flex items-center justify-center"
                    aria-label="Delete note"
                    title="Delete this note"
                  >
                    <X size={10} weight="bold" />
                  </button>
                </div>
                <button
                  type="button"
                  data-testid={`track-${track.id}-onset-${i}`}
                  data-selected={selectedIndices.has(i) ? "true" : "false"}
                  onMouseDown={(e) => beginDrag(e, i)}
                  onClick={(e) => handleNoteClick(e, i)}
                  onContextMenu={(e) => openNoteCtx(e, i)}
                  aria-label={`Note ${i + 1}: click to select, shift-click to multi-select, drag to nudge, drag right edge to resize, right-click for more`}
                  className="absolute inset-0 rounded transition-colors"
                  style={(() => {
                    const dragging = isDragging || isGroupMember || isResizing;
                    if (dragging) {
                      return {
                        background: "rgba(192,132,252,0.30)",
                        border: "1px solid #C084FC",
                        boxShadow: "0 0 12px rgba(192,132,252,0.6)",
                        cursor: isResizing ? "ew-resize" : "grabbing",
                      };
                    }
                    if (isSelected) {
                      return {
                        background: "rgba(96,165,250,0.30)",
                        border: "1px solid #60A5FA",
                        boxShadow: "inset 2px 0 0 #60A5FA, 0 0 8px rgba(96,165,250,0.5)",
                        cursor: "grab",
                      };
                    }
                    return {
                      background: hasCustomLength ? "rgba(168,255,31,0.20)" : "rgba(168,255,31,0.12)",
                      border: hasCustomLength ? "1px solid rgba(168,255,31,0.85)" : "1px solid rgba(168,255,31,0.6)",
                      boxShadow: "inset 2px 0 0 #A8FF1F",
                      cursor: "grab",
                    };
                  })()}
                />
                {/* Right-edge resize grabber — 6 px column with ew-resize cursor.
                    Only visible on hover to keep the block clean when idle.
                    Double-click = "Match to next": snap length so the block ends
                    exactly at the next onset (or loop end for the last note),
                    minus the standard 20 ms breathing room. */}
                <div
                  data-testid={`track-${track.id}-onset-${i}-resize`}
                  onMouseDown={(e) => beginResize(e, i)}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!onResizeOnset) return;
                    const orig = onsets[i];
                    const nextOn = i + 1 < onsets.length ? onsets[i + 1] : loopDuration;
                    const targetLen = Math.max(0.02, nextOn - orig - 0.02);
                    onResizeOnset(i, targetLen);
                  }}
                  className="absolute top-0 right-0 h-full w-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    cursor: "ew-resize",
                    background: "linear-gradient(90deg, transparent, rgba(168,255,31,0.85))",
                  }}
                  title="Drag to set how long the note rings · double-click to match next note"
                  aria-label={`Resize note ${i + 1} — double-click to fill to next onset`}
                />
                {/* Classification override badge (K/S/H) — solid, bright: only
                    shown when the user has manually overridden this hit's
                    routing for Drumify. */}
                {hitOverrides[String(i)] && (
                  <div
                    data-testid={`track-${track.id}-onset-${i}-class-badge`}
                    className="absolute top-0.5 right-0.5 pointer-events-none text-[9px] font-mono font-bold uppercase tracking-widest px-1 rounded"
                    style={{
                      background: "#0A0A0A",
                      color: hitOverrides[String(i)] === "crash" ? "#FBBF24" : "#A8FF1F",
                      border: `1px solid ${hitOverrides[String(i)] === "crash" ? "#FBBF24" : "#A8FF1F"}`,
                      lineHeight: "12px",
                    }}
                    title={hitOverrides[String(i)] === "crash"
                      ? "Crash accent — plays a crash cymbal on any Drumify kit"
                      : `Drumify override: ${hitOverrides[String(i)]}`}
                  >
                    {hitOverrides[String(i)] === "kick" && "K"}
                    {hitOverrides[String(i)] === "snare" && "S"}
                    {hitOverrides[String(i)] === "hat" && "H"}
                    {hitOverrides[String(i)] === "crash" && "C"}
                  </div>
                )}
                {/* Classifier suggestion (K/S/H) — dim, no background: shown
                    ONLY when there's no user override yet, so producers can see
                    at a glance where the auto-classifier would send each hit
                    for Drumify (Acoustic/Lo-fi kits). Colour-coded by voicing:
                    orange=kick, yellow=snare, blue=hat. */}
                {!hitOverrides[String(i)] && suggestions[i] && (
                  <div
                    data-testid={`track-${track.id}-onset-${i}-class-suggest`}
                    className="absolute top-0.5 right-0.5 pointer-events-none text-[9px] font-mono font-bold uppercase tracking-widest px-1"
                    style={{
                      color: {
                        kick:  "rgba(249,115,22,0.55)",
                        snare: "rgba(250,204,21,0.60)",
                        hat:   "rgba(96,165,250,0.55)",
                      }[suggestions[i]],
                      lineHeight: "12px",
                    }}
                    title={`Classifier suggestion: ${suggestions[i]} (right-click to override)`}
                  >
                    {suggestions[i] === "kick" && "K"}
                    {suggestions[i] === "snare" && "S"}
                    {suggestions[i] === "hat" && "H"}
                  </div>
                )}
              </div>
            );
          }
          return (
            <div
              key={`onset-${track.id}-${i}`}
              data-testid={`track-${track.id}-onset-${i}`}
              className="absolute top-0 w-px"
              style={{
                left: `${leftPct}%`,
                height: "8px",
                background: "#A8FF1F",
                boxShadow: "0 0 4px rgba(168,255,31,0.7)",
              }}
            />
          );
        })}
      </div>
      {/* Drag preview: snap line + delta badge */}
      {drag && (
        <>
          <div
            className="absolute top-0 bottom-0 w-px pointer-events-none"
            data-testid={`track-${track.id}-nudge-snap`}
            style={{
              left: `${(drag.currentTime / loopDuration) * 100}%`,
              background: "#C084FC",
              boxShadow: "0 0 10px rgba(192,132,252,0.8)",
            }}
          />
          <div
            className="absolute top-6 px-2 py-1 rounded-full bg-[#C084FC] text-[#0A0A0A] text-[10px] font-mono uppercase tracking-widest pointer-events-none"
            style={{
              left: `${(drag.currentTime / loopDuration) * 100}%`,
              transform: "translate(-50%, 0)",
            }}
          >
            {(() => {
              const dtMs = Math.round((drag.currentTime - drag.originalTime) * 1000);
              return `${dtMs >= 0 ? "+" : ""}${dtMs}ms`;
            })()}
          </div>
        </>
      )}
      {/* Playhead */}
      {playing && (
        <motion.div
          className="absolute top-0 bottom-0 w-px"
          data-testid={`track-${track.id}-playhead`}
          style={{
            background: "#A8FF1F",
            boxShadow: "0 0 10px rgba(168,255,31,0.6)",
            left: `${pct * 100}%`,
          }}
        />
      )}
      {/* Right-click "Duplicate to bar N" menu */}
      {noteCtx && (
        <div
          data-testid={`track-${track.id}-note-ctx-menu`}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute z-40 bg-[#1A1B1D] border border-[#34373B] rounded-2xl py-1.5 px-1 shadow-2xl min-w-[180px]"
          style={{
            left: `${Math.min(noteCtx.x, Math.max(0, (timelineRef.current?.clientWidth || 0) - 200))}px`,
            top: `${Math.min(noteCtx.y, Math.max(0, (timelineRef.current?.clientHeight || 0) - 460))}px`,
          }}
        >
          <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-widest font-mono text-[#9CA0A6]">
            Duplicate to…
          </div>
          {Array.from({ length: loopBars }, (_, b) => {
            const isSrc = b === ctxSourceBar;
            return (
              <button
                key={`ctx-bar-${b}`}
                type="button"
                disabled={isSrc}
                onClick={() => {
                  if (onDuplicateToBars) onDuplicateToBars(noteCtx.onsetIndex, [b]);
                  setNoteCtx(null);
                }}
                data-testid={`track-${track.id}-note-ctx-bar-${b + 1}`}
                className="w-full text-left px-3 py-1.5 text-sm font-mono rounded-lg text-white hover:bg-[#60A5FA]/15 disabled:text-[#5A5E63] disabled:cursor-not-allowed transition-colors"
              >
                Bar {b + 1}{isSrc ? "  · current" : ""}
              </button>
            );
          })}
          <div className="border-t border-[#34373B] my-1" />
          <button
            type="button"
            onClick={() => {
              const others = Array.from({ length: loopBars }, (_, b) => b).filter((b) => b !== ctxSourceBar);
              if (onDuplicateToBars) onDuplicateToBars(noteCtx.onsetIndex, others);
              setNoteCtx(null);
            }}
            data-testid={`track-${track.id}-note-ctx-all`}
            className="w-full text-left px-3 py-1.5 text-sm font-mono rounded-lg text-[#A8FF1F] hover:bg-[#A8FF1F]/15 transition-colors"
          >
            All other bars
          </button>
          {onClassifyOnset && (
            <>
              <div className="border-t border-[#34373B] my-1" />
              <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-widest font-mono text-[#9CA0A6]">
                Classify as… <span className="text-[#5A5E63] normal-case tracking-normal">(Drumify)</span>
              </div>
              {[
                { v: "kick",  l: "Kick",   c: "#F97316" },
                { v: "snare", l: "Snare",  c: "#FACC15" },
                { v: "hat",   l: "Hi-hat", c: "#60A5FA" },
                { v: "crash", l: "Crash accent", c: "#FBBF24", hint: "any kit" },
              ].map((opt) => {
                const active = hitOverrides[String(noteCtx.onsetIndex)] === opt.v;
                return (
                  <button
                    key={`ctx-cls-${opt.v}`}
                    type="button"
                    onClick={() => {
                      onClassifyOnset(noteCtx.onsetIndex, opt.v);
                      setNoteCtx(null);
                    }}
                    data-testid={`track-${track.id}-note-ctx-classify-${opt.v}`}
                    className="w-full text-left px-3 py-1.5 text-sm font-mono rounded-lg hover:bg-white/5 transition-colors flex items-center gap-2"
                    style={{ color: active ? opt.c : "#E5E7EB" }}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: opt.c, opacity: active ? 1 : 0.5 }}
                    />
                    {opt.l}
                    {opt.hint && !active && <span className="ml-auto text-[10px] uppercase tracking-widest text-[#5A5E63]">{opt.hint}</span>}
                    {active && <span className="ml-auto text-[10px] uppercase tracking-widest text-[#9CA0A6]">current</span>}
                  </button>
                );
              })}
              {hitOverrides[String(noteCtx.onsetIndex)] && (
                <button
                  type="button"
                  onClick={() => {
                    onClassifyOnset(noteCtx.onsetIndex, "auto");
                    setNoteCtx(null);
                  }}
                  data-testid={`track-${track.id}-note-ctx-classify-auto`}
                  className="w-full text-left px-3 py-1.5 text-sm font-mono rounded-lg text-[#9CA0A6] hover:bg-white/5 hover:text-white transition-colors"
                >
                  Reset to auto
                </button>
              )}
            </>
          )}
          {onPitchShiftOnset && (
            <>
              <div className="border-t border-[#34373B] my-1" />
              <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-widest font-mono text-[#9CA0A6]">
                Pitch shift <span className="text-[#5A5E63] normal-case tracking-normal">(off-key fix)</span>
              </div>
              <div className="px-2 pb-1 grid grid-cols-4 gap-1">
                {[
                  { s: -12, l: "−12" },
                  { s:  -1, l: "−1"  },
                  { s:  +1, l: "+1"  },
                  { s: +12, l: "+12" },
                ].map((opt) => (
                  <button
                    key={`ctx-pitch-${opt.s}`}
                    type="button"
                    onClick={() => {
                      onPitchShiftOnset(noteCtx.onsetIndex, opt.s);
                      setNoteCtx(null);
                    }}
                    data-testid={`track-${track.id}-note-ctx-pitch-${opt.s > 0 ? "up" : "down"}${Math.abs(opt.s)}`}
                    className="text-xs font-mono tabular-nums px-2 py-1 rounded bg-[#22C55E]/15 text-[#4ADE80] border border-[#4ADE80]/50 hover:bg-[#22C55E]/30 hover:border-[#4ADE80] transition-colors"
                    title={`Shift pitch ${opt.s > 0 ? "up" : "down"} ${Math.abs(opt.s)} semitone${Math.abs(opt.s) === 1 ? "" : "s"}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
